import React, {
	Children,
	cloneElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import {
	areEqual,
	FixedSizeList,
	type FixedSizeListProps,
	type ListChildComponentProps,
} from 'react-window';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {styled, Theme} from '@netx/core-components/Theme';

type FixedSizeListEnforcedProps = Required<FixedSizeListProps>;

const LISTBOX_PADDING = 8;
const ADDITIONAL_ITEM_HEIGHT = 2 * LISTBOX_PADDING;

const styles = (theme: Theme) =>
	createStyles({
		'& .MuiListItemSecodaryAction-root': {
			paddingLeft: 8,
		},
	});

export interface OuterElementProps {
	'aria-labelledby': string;
	className: string;
	id: string;
	onMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
	role: string;
}
const OuterElementContext = React.createContext<Partial<OuterElementProps>>({});
const OuterElementType = React.forwardRef(
	(props, ref: React.Ref<HTMLDivElement>) => {
		const outerProps = useContext(OuterElementContext);
		return <div ref={ref} {...props} {...outerProps} />;
	},
);

const InnerElementRoot = styled('ul')({
	padding: 0,
	margin: 0,
});

const renderRow = React.memo(
	({data, index, style}: ListChildComponentProps) => {
		return cloneElement(data[index], {
			style: {
				...style,
				top: (style.top as number) + LISTBOX_PADDING,
			},
		});
	},
	areEqual,
);
export interface ListboxComponentProps
	extends React.PropsWithChildren<OuterElementProps> {
	hasSecondaryText?: boolean;
	inputRef: React.MutableRefObject<HTMLInputElement | null>;
	listRef?: React.MutableRefObject<HTMLUListElement | null>;
	fixedSizeListRef?: React.MutableRefObject<FixedSizeList | null>;
}

/**
 * Adapter for react-window's `VariableSizeList`
 * @private
 */
const ListboxComponent = React.forwardRef<
	HTMLDivElement,
	WithStyles<typeof styles> & ListboxComponentProps
>(
	(
		{
			classes,
			children,
			hasSecondaryText,
			inputRef,
			listRef,
			fixedSizeListRef,
			...props
		},
		ref,
	) => {
		const itemData = Children.toArray(children);
		const itemCount = itemData.length;
		const itemSize = hasSecondaryText ? 56 : 48;

		const innerRef = useSharedRef<HTMLDivElement | null>(null, [ref]);
		const innerFixedSizeListRef = useSharedRef<FixedSizeList | null>(null, [
			fixedSizeListRef,
		]);

		const justLoopedToRef = useRef(-1);

		// The autocomplete dropdown is a little funky with keyboard navigation
		// and virtualization
		useEffect(() => {
			const handleKeydown = (event: KeyboardEvent) => {
				if (!innerFixedSizeListRef.current || !inputRef?.current) return;
				let prevent = false;
				const itemCount = innerFixedSizeListRef.current.props.itemCount;
				const activeIndex = parseInt(
					(
						inputRef.current.getAttribute('aria-activedescendant') || '-1'
					)?.replace(/^.*-option-/, ''),
				);
				if (activeIndex < 0) return;

				justLoopedToRef.current = -1;
				switch (event.key) {
					case 'ArrowUp': {
						if (activeIndex === 0 && itemCount > 1) {
							justLoopedToRef.current = itemCount - 1;
							innerFixedSizeListRef.current.scrollTo(
								(itemCount - 1) * itemSize + LISTBOX_PADDING,
							);
							prevent = true;
						}
						break;
					}
					case 'ArrowDown': {
						if (activeIndex === itemCount - 1 && itemCount > 1) {
							justLoopedToRef.current = 0;
							innerFixedSizeListRef.current.scrollTo(0);
							prevent = true;
						}
						break;
					}
					case 'End':
						justLoopedToRef.current = itemCount - 1;
						innerFixedSizeListRef.current.scrollTo(
							(itemCount - 1) * itemSize + LISTBOX_PADDING,
						);
						prevent = true;
						break;
					case 'Home':
						justLoopedToRef.current = 0;
						innerFixedSizeListRef.current.scrollTo(0);
						prevent = true;
						break;
				}
				if (prevent) {
					event.preventDefault();
					event.stopPropagation();
				}
			};
			document.addEventListener('keydown', handleKeydown, {capture: true});
			return () => {
				document.removeEventListener('keydown', handleKeydown, {capture: true});
			};
		}, []);

		// The focus event from the underlying `useAutocomplete` is firing before the item can be rendered
		// because the call to render comes after the mouse event
		const handleItemRendered = useCallback<
			FixedSizeListEnforcedProps['onItemsRendered']
		>((props) => {
			if (!innerFixedSizeListRef.current || !innerRef.current) return;

			const event = new MouseEvent('mouseover', {
				bubbles: true,
				cancelable: true,
			});

			const hasFocused = () =>
				innerRef.current?.querySelector('[data-focus="true"]') !== null;

			if (justLoopedToRef.current > -1) {
				const option = innerRef.current?.querySelector(
					`[id*="option-${justLoopedToRef.current}"]`,
				);
				if (option && !hasFocused()) option.dispatchEvent(event);
				justLoopedToRef.current = -1;
			}
		}, []);

		// if you give it the actual height of what all the items would be
		// it will actually render them all - just keep in mind:
		// this is just the height of the content we want to display/in the
		// DOM
		const height = useMemo(
			() => (itemCount > 8 ? 8 * itemSize : itemCount * itemSize), //itemData.map(getChildSize).reduce((a, b) => a + b, 0),
			[itemCount, itemSize],
		);

		return (
			<div ref={innerRef}>
				<OuterElementContext.Provider value={props}>
					<FixedSizeList
						height={height + ADDITIONAL_ITEM_HEIGHT}
						innerElementType={InnerElementRoot}
						itemCount={itemCount}
						itemData={itemData}
						itemSize={itemSize}
						outerElementType={OuterElementType}
						overscanCount={8}
						onItemsRendered={handleItemRendered}
						ref={innerFixedSizeListRef}
						width="100%"
					>
						{renderRow}
					</FixedSizeList>
				</OuterElementContext.Provider>
			</div>
		);
	},
);

export default withStyles(styles)(React.memo(ListboxComponent));
